import { API_BASE } from '../../lib/apiBase';
import type { StagedCreateImage } from './detail/catalogProductDetailHelpers';

/** API field → étape assistant création (`wizardSteps`). */
export const CREATE_WIZARD_FIELD_STEP: Record<string, string> = {
  name: 'Identité',
  slug: 'Identité',
  shelfName: 'Identité',
  productTypeCode: 'Classification',
  categoryId: 'Classification',
  markeId: 'Marque',
  variantAxes: 'Variantes',
  description: 'Marque',
  details: 'Détails',
  tags: 'Tags',
};

export function pickFirstWizardErrorJump(
  errors: Record<string, string>,
  wizardSteps: readonly string[],
): { stepIndex: number } | null {
  const keys = Object.keys(errors).filter((k) => k !== '_form');
  if (keys.length === 0) return null;

  const priority = ['name', 'slug', 'shelfName', 'productTypeCode', 'categoryId', 'tags', 'markeId', 'variantAxes', 'description', 'details'];
  const ordered = [...priority.filter((k) => keys.includes(k)), ...keys.filter((k) => !priority.includes(k))];

  for (const key of ordered) {
    let stepName = CREATE_WIZARD_FIELD_STEP[key];
    if (!stepName) {
      if (/slug|^name$/i.test(key) || key.endsWith('.name')) stepName = 'Identité';
      else if (/categor|productType|typeCode/i.test(key)) stepName = 'Classification';
      else if (/^tags?/i.test(key)) stepName = 'Tags';
      else if (/marke/i.test(key)) stepName = 'Marque';
      else if (/variant|matrix|option/i.test(key)) stepName = 'Variantes';
      else if (/^details?/i.test(key) || key.startsWith('details')) stepName = 'Détails';
      else if (/desc|channel|visible|web|show/i.test(key)) stepName = 'Marque';
      else if (/media|image|thumbnail/i.test(key)) stepName = 'Images';
    }
    if (!stepName) continue;
    const stepIndex = wizardSteps.indexOf(stepName);
    if (stepIndex >= 0) return { stepIndex };
  }
  return { stepIndex: 0 };
}

export function isAllowedImageFile(file: File): boolean {
  if (file.type?.startsWith('image/')) return true;
  // Some browsers/devices provide empty mime; fall back to filename extension.
  const name = file.name?.toLowerCase() ?? '';
  return /\.(jpe?g|png|webp|gif|avif)$/.test(name);
}

function newStagedCreateImageId(): string {
  try {
    if (typeof crypto?.randomUUID === 'function') return crypto.randomUUID();
  } catch {
    /* non-secure context */
  }
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

/** Fetch OFF/external image via API proxy (avoids browser CORS). Throws on proxy/HTTP failure. */
export async function fetchCatalogProxyImageFile(
  imageUrl: string,
  token: string,
  isAllowed: (file: File) => boolean = isAllowedImageFile,
): Promise<File | null> {
  const proxy = `${API_BASE}/catalog/admin/external-catalog/image?url=${encodeURIComponent(imageUrl)}`;
  const res = await fetch(proxy, {
    headers: { Authorization: `Bearer ${token}` },
    credentials: 'include',
    cache: 'no-store',
  });
  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try {
      const body = (await res.json()) as { message?: string | string[] };
      const msg = body?.message;
      if (typeof msg === 'string' && msg.trim()) detail = msg.trim();
      else if (Array.isArray(msg) && msg[0]) detail = String(msg[0]);
    } catch {
      /* non-JSON */
    }
    throw new Error(detail);
  }
  const blob = await res.blob();
  if (!blob.type.startsWith('image/')) {
    throw new Error('Réponse proxy non-image.');
  }
  const ext = blob.type.split('/')[1]?.split('+')[0] || 'jpg';
  const file = new File([blob], `off-${Date.now()}.${ext}`, { type: blob.type });
  if (!isAllowed(file)) {
    throw new Error('Type image non supporté.');
  }
  return file;
}

export async function fetchStagedCreateImageFromCatalogProxy(
  imageUrl: string,
  token: string,
  isAllowed: (file: File) => boolean = isAllowedImageFile,
): Promise<StagedCreateImage | null> {
  try {
    const file = await fetchCatalogProxyImageFile(imageUrl, token, isAllowed);
    if (!file) return null;
    return {
      id: newStagedCreateImageId(),
      source: 'file',
      file,
      previewUrl: URL.createObjectURL(file),
    };
  } catch {
    return null;
  }
}

export async function uploadBrandLogoFromExternalUrl(opts: {
  brandLogoUrl: string;
  token: string;
  upload: (file: File) => Promise<{ key: string; url: string }>;
}): Promise<{ key: string; url: string } | null> {
  try {
    const file = await fetchCatalogProxyImageFile(opts.brandLogoUrl, opts.token);
    if (!file) return null;
    const created = await opts.upload(file);
    return { key: created.key, url: created.url };
  } catch {
    return null;
  }
}
